import { normalizeOrder } from "./orders";
import type { Order } from "../types";

export type CheckoutQuote = {
  quoteId: string; itemCents: number; protectionCents: number; shippingCents: number;
  shippingHandlingCents: number; totalCents: number; currency: "EUR"; checkoutEnabled: boolean; expiresAt: string;
  shipping: { carrier: string; service: string; insuranceCoverageCents: number; expiresAt: string; estimatedBusinessDays: number };
};
const record = (value: unknown): Record<string, unknown> | null => value !== null && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : null;
const cents = (value: unknown): value is number => typeof value === "number" && Number.isSafeInteger(value) && value >= 0;

export function normalizeCheckoutQuote(response: unknown): CheckoutQuote {
  const envelope = record(response); const quote = record(envelope?.quote); const shipping = record(quote?.shipping);
  if (!quote || !shipping || typeof quote.quoteId !== "string" || !quote.quoteId || quote.quoteId.length > 65536
    || quote.currency !== "EUR" || typeof quote.checkoutEnabled !== "boolean"
    || !cents(quote.itemCents) || quote.itemCents === 0 || !cents(quote.protectionCents) || !cents(quote.shippingCents) || !cents(quote.shippingHandlingCents) || !cents(quote.totalCents)
    || quote.totalCents !== quote.itemCents + quote.protectionCents + quote.shippingCents + quote.shippingHandlingCents
    || !cents(shipping.amountCents) || shipping.amountCents !== quote.shippingCents
    || !cents(shipping.insuranceCoverageCents) || shipping.insuranceCoverageCents < quote.itemCents
    || typeof shipping.carrier !== "string" || !shipping.carrier || typeof shipping.service !== "string" || !shipping.service
    || typeof shipping.expiresAt !== "string" || !Number.isFinite(Date.parse(shipping.expiresAt))
    || typeof quote.expiresAt !== "string" || !Number.isFinite(Date.parse(quote.expiresAt)) || Date.parse(quote.expiresAt) > Date.parse(shipping.expiresAt)
    || shipping.tracked !== true || typeof shipping.estimatedBusinessDays !== "number" || !Number.isInteger(shipping.estimatedBusinessDays) || shipping.estimatedBusinessDays < 1 || shipping.estimatedBusinessDays > 30) {
    throw new Error("Il preventivo non è verificabile. Calcola nuovamente i costi prima di procedere.");
  }
  return {
    quoteId: quote.quoteId, itemCents: quote.itemCents, protectionCents: quote.protectionCents,
    shippingCents: quote.shippingCents, shippingHandlingCents: quote.shippingHandlingCents, totalCents: quote.totalCents,
    currency: "EUR", checkoutEnabled: quote.checkoutEnabled, expiresAt: quote.expiresAt,
    shipping: { carrier: shipping.carrier, service: shipping.service, insuranceCoverageCents: shipping.insuranceCoverageCents, expiresAt: shipping.expiresAt, estimatedBusinessDays: shipping.estimatedBusinessDays },
  };
}

/** The signed opaque quote token is returned unchanged, never interpreted. */
export function quoteConfirmation(quote: CheckoutQuote | null, now = Date.now()) {
  if (!quote || !quote.checkoutEnabled || !Number.isFinite(Date.parse(quote.expiresAt)) || Date.parse(quote.expiresAt) <= now) {
    throw new Error("Il preventivo è scaduto o il pagamento non è disponibile. Calcola nuovamente i costi e conferma il nuovo riepilogo.");
  }
  return { quoteId: quote.quoteId, expectedTotalCents: quote.totalCents, expectedCurrency: quote.currency };
}

export type EmbeddedCheckout = {
  provider: "stripe"; uiMode: "embedded"; sessionId: string; clientSecret: string;
  publishableKey: string; expiresAt: string; klarnaEligible: boolean;
};
export type EmbeddedPurchase = { checkout: EmbeddedCheckout; order: Order };

/** Stripe's client secret is opaque: do not infer its encoding from old examples. */
export function isEmbeddedClientSecret(value: unknown, sessionId: string): value is string {
  if (typeof value !== "string") return false;
  const prefix = `${sessionId}_secret_`;
  const bytes = new TextEncoder().encode(value).byteLength;
  return value.startsWith(prefix) && bytes >= prefix.length + 8 && bytes <= 4096
    && !Array.from(value).some(character => { const code = character.codePointAt(0)!; return code <= 32 || code === 127; });
}
export type ExpectedPurchase = { buyerId: string; sellerId: string; listingId: string; totalCents: number; orderId?: string };
const internalId = /^[a-f0-9]{32}$/;
const paymentStates = new Set(["awaiting_payment", "payment_review", "paid", "shipped", "delivered", "completed", "disputed", "refund_pending", "refunded", "cancelled"]);

/** Only the server's authenticated order response is evidence of payment. */
export function normalizePaymentOrder(value: unknown, expected: ExpectedPurchase): Order {
  const raw = record(value);
  if (!raw || typeof raw.id !== "string" || !internalId.test(raw.id)
    || (expected.orderId && raw.id !== expected.orderId)
    || raw.buyerId !== expected.buyerId || raw.sellerId !== expected.sellerId || raw.listingId !== expected.listingId
    || !cents(raw.totalCents) || raw.totalCents !== expected.totalCents
    || typeof raw.status !== "string" || !paymentStates.has(raw.status)
    || typeof raw.createdAt !== "string" || !Number.isFinite(Date.parse(raw.createdAt))) {
    throw new Error("Il pagamento non corrisponde a questo ordine. Controlla il tuo account prima di riprovare.");
  }
  return normalizeOrder(raw);
}

export function normalizeEmbeddedPurchase(response: unknown, expected: ExpectedPurchase, now = Date.now()): EmbeddedPurchase {
  const envelope = record(response); const checkout = record(envelope?.checkout);
  const order = normalizePaymentOrder(envelope?.order, expected);
  const sessionMode = typeof checkout?.sessionId === "string" ? /^cs_(test|live)_[A-Za-z0-9_]+$/.exec(checkout.sessionId)?.[1] : null;
  const keyMode = typeof checkout?.publishableKey === "string" ? /^pk_(test|live)_[A-Za-z0-9]{16,240}$/.exec(checkout.publishableKey)?.[1] : null;
  if (!checkout || checkout.provider !== "stripe" || checkout.uiMode !== "embedded" || !sessionMode || sessionMode !== keyMode
    || typeof checkout.sessionId !== "string" || checkout.sessionId.length > 256
    || !isEmbeddedClientSecret(checkout.clientSecret, checkout.sessionId)
    || typeof checkout.publishableKey !== "string" || typeof checkout.expiresAt !== "string"
    || !Number.isFinite(Date.parse(checkout.expiresAt)) || Date.parse(checkout.expiresAt) <= now
    || (checkout.klarnaEligible !== undefined && typeof checkout.klarnaEligible !== "boolean")
    || order.status !== "awaiting_payment" || !order.checkoutExpiresAt
    || Date.parse(order.checkoutExpiresAt) !== Date.parse(checkout.expiresAt)) {
    throw new Error("Il modulo di pagamento non è disponibile o è scaduto. Riprendi questo ordine dal tuo account.");
  }
  return { order, checkout: {
    provider: "stripe", uiMode: "embedded", sessionId: checkout.sessionId, clientSecret: checkout.clientSecret,
    publishableKey: checkout.publishableKey, expiresAt: checkout.expiresAt, klarnaEligible: checkout.klarnaEligible === true,
  } };
}

/** The return URL contains an internal order ID only, never payment credentials. */
export function paymentReturnOrder(search: string): string | null {
  const params = new URLSearchParams(search); const id = params.get("order");
  return params.get("checkout") === "return" && id && internalId.test(id) ? id : null;
}

